/**
 * Durable Asset Inventory filter contract for Security Controls coverage.
 * URL is the source of truth — do not rely on React state alone.
 *
 * Query params:
 *   tab=inventory
 *   securityControl=<CONTROL_TYPE>
 *   controlStatus=<STATUS>
 *   securityCoverage=<known|verified|unknown|gap|all>
 *   verificationState=<STATE>
 *   sourceQuality=<QUALITY>   (alias of verificationState / data-quality buckets)
 */

import { normalizeControlType } from './securityControlCoverageNormalize'

export type SecurityCoverageMode = 'known' | 'verified' | 'unknown' | 'gap' | 'all'

export type SecurityControlInventoryFilters = {
  securityControl: string | null
  controlStatus: string | null
  securityCoverage: SecurityCoverageMode | null
  verificationState: string | null
  sourceQuality: string | null
  showSecurityControls: boolean
}

export type AssetControlRow = {
  controlType: string
  status?: string
  verificationState?: string
  sourceType?: string
  sourceProvider?: string | null
  lastCheckedAt?: string | null
}

export type AssetSecurityCoverageFields = {
  id?: string
  securityControlCoverage?: { controls?: AssetControlRow[] }
  unknownControlsCount?: number
  disabledControlsCount?: number
  verifiedControlsCount?: number
  knownControlsCount?: number
  securityCoverageStatus?: string
}

const KNOWN_STATUSES = new Set(['ENABLED', 'DISABLED', 'PARTIAL'])
const VERIFIED_LIKE = new Set(['VERIFIED', 'DISCOVERED'])

const COVERAGE_MODES = new Set<SecurityCoverageMode>([
  'known',
  'verified',
  'unknown',
  'gap',
  'all',
])

export function normalizeControlStatus(raw: string | null | undefined): string | null {
  const v = String(raw || '')
    .trim()
    .toUpperCase()
  if (!v) return null
  if (['ENABLED', 'DISABLED', 'PARTIAL', 'UNKNOWN', 'NOT_APPLICABLE'].includes(v)) return v
  return v
}

export function normalizeVerificationState(raw: string | null | undefined): string | null {
  const v = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
  if (!v) return null
  const aliases: Record<string, string> = {
    SELF_REPORTED: 'SELF_REPORTED',
    SELFREPORTED: 'SELF_REPORTED',
    MANUAL: 'SELF_REPORTED',
    MANUALLY_VERIFIED: 'MANUALLY_VERIFIED',
    VERIFIED: 'VERIFIED',
    DISCOVERED: 'DISCOVERED',
    STALE: 'STALE',
    UNKNOWN: 'UNKNOWN',
  }
  return aliases[v] || v
}

/** Map sourceQuality URL values onto verification / quality buckets. */
export function normalizeSourceQuality(raw: string | null | undefined): string | null {
  const v = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
  if (!v) return null
  const aliases: Record<string, string> = {
    SELF_REPORTED: 'SELF_REPORTED',
    SELFREPORTED: 'SELF_REPORTED',
    VERIFIED: 'VERIFIED',
    STALE: 'STALE',
    UNKNOWN: 'UNKNOWN',
    DISCOVERED: 'DISCOVERED',
    MANUALLY_VERIFIED: 'MANUALLY_VERIFIED',
  }
  return aliases[v] || v
}

export function normalizeSecurityCoverage(
  raw: string | null | undefined,
): SecurityCoverageMode | null {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
  if (!v) return null
  if (COVERAGE_MODES.has(v as SecurityCoverageMode)) return v as SecurityCoverageMode
  return null
}

export function parseSecurityControlInventoryFilters(
  searchParams: URLSearchParams | { get: (k: string) => string | null },
): SecurityControlInventoryFilters {
  const show =
    searchParams.get('showSecurityControls') === 'true' ||
    searchParams.get('showSecurityControls') === '1'
  return {
    securityControl: normalizeControlType(searchParams.get('securityControl')),
    controlStatus: normalizeControlStatus(searchParams.get('controlStatus')),
    securityCoverage: normalizeSecurityCoverage(searchParams.get('securityCoverage')),
    verificationState: normalizeVerificationState(searchParams.get('verificationState')),
    sourceQuality: normalizeSourceQuality(searchParams.get('sourceQuality')),
    showSecurityControls: show,
  }
}

export function hasActiveSecurityControlFilters(f: SecurityControlInventoryFilters): boolean {
  return Boolean(
    f.securityControl ||
      f.controlStatus ||
      f.securityCoverage ||
      f.verificationState ||
      f.sourceQuality ||
      f.showSecurityControls,
  )
}

export type BuildInventoryUrlOpts = {
  controlType?: string | null
  controlStatus?: string | null
  securityCoverage?: SecurityCoverageMode | boolean | null
  /** @deprecated use securityCoverage: 'gap' */
  coverageGap?: boolean
  /** @deprecated use securityCoverage: 'unknown' */
  coverageUnknown?: boolean
  verificationState?: string | null
  sourceQuality?: string | null
  showSecurityControls?: boolean
}

/** Build durable `/assets?tab=inventory&...` URL. */
export function buildSecurityControlInventoryUrl(opts: BuildInventoryUrlOpts = {}): string {
  const params = buildSecurityControlSearchParams(opts)
  return `/assets?${params.toString()}#assets-inventory-results`
}

/** Search params only — for in-page `setSearchParams` without remounting the route. */
export function buildSecurityControlSearchParams(opts: BuildInventoryUrlOpts = {}): URLSearchParams {
  const params = new URLSearchParams()
  params.set('tab', 'inventory')

  let coverage: SecurityCoverageMode | null = null
  if (opts.coverageGap) coverage = 'gap'
  else if (opts.coverageUnknown) coverage = 'unknown'
  else if (opts.securityCoverage === true) coverage = 'all'
  else if (typeof opts.securityCoverage === 'string') {
    coverage = normalizeSecurityCoverage(opts.securityCoverage)
  }

  const control = normalizeControlType(opts.controlType ?? null)
  const status = normalizeControlStatus(opts.controlStatus ?? null)
  const verification = normalizeVerificationState(opts.verificationState ?? null)
  const quality = normalizeSourceQuality(opts.sourceQuality ?? null)

  if (control) params.set('securityControl', control)
  if (status) params.set('controlStatus', status)
  if (coverage) params.set('securityCoverage', coverage)
  if (verification) params.set('verificationState', verification)
  if (quality) params.set('sourceQuality', quality)
  if (opts.showSecurityControls || coverage === 'all') {
    params.set('showSecurityControls', 'true')
  }

  return params
}

function controlsOf(asset: AssetSecurityCoverageFields): AssetControlRow[] {
  return asset.securityControlCoverage?.controls || []
}

function isVerifiedLike(row: AssetControlRow): boolean {
  const vs = String(row.verificationState || '').toUpperCase()
  if (vs === 'STALE') return false
  return VERIFIED_LIKE.has(vs)
}

function isKnownStatus(status: string | undefined): boolean {
  return KNOWN_STATUSES.has(String(status || '').toUpperCase())
}

function rowMatchesVerification(row: AssetControlRow, state: string): boolean {
  const vs = String(row.verificationState || 'UNKNOWN').toUpperCase()
  if (state === 'VERIFIED') return isVerifiedLike(row)
  return vs === state
}

function rowMatchesQuality(row: AssetControlRow, quality: string): boolean {
  if (quality === 'VERIFIED') return isVerifiedLike(row)
  if (quality === 'SELF_REPORTED') {
    const vs = String(row.verificationState || '').toUpperCase()
    const src = String(row.sourceType || '').toUpperCase()
    return (
      vs === 'SELF_REPORTED' ||
      src === 'MANUAL_ASSET_POSTURE' ||
      src === 'LEGACY_ASSET_POSTURE' ||
      src === 'SELF_REPORTED'
    )
  }
  if (quality === 'STALE') return String(row.verificationState || '').toUpperCase() === 'STALE'
  if (quality === 'UNKNOWN') {
    return (
      !isKnownStatus(row.status) ||
      String(row.verificationState || 'UNKNOWN').toUpperCase() === 'UNKNOWN'
    )
  }
  return rowMatchesVerification(row, quality)
}

/**
 * Match one asset against the durable inventory filter contract.
 * Uses only authoritative per-asset security-control DTO fields.
 */
export function assetMatchesSecurityControlFilters(
  asset: AssetSecurityCoverageFields,
  filters: SecurityControlInventoryFilters,
): boolean {
  if (!hasActiveSecurityControlFilters(filters)) return true

  const coverage = filters.securityCoverage
  const controlType = filters.securityControl
  const statusFilter = filters.controlStatus
  const verification = filters.verificationState
  const quality = filters.sourceQuality
  const rows = controlsOf(asset)

  // Asset-level coverage modes (when no control selected, or as a pre-gate)
  if (coverage === 'gap') {
    // Must match card "Requires attention" = disabledControlsCount > 0
    if ((asset.disabledControlsCount ?? 0) <= 0) return false
  } else if (coverage === 'unknown') {
    if ((asset.knownControlsCount ?? 0) !== 0) return false
  } else if (coverage === 'known' && !controlType) {
    if ((asset.knownControlsCount ?? 0) <= 0) return false
  } else if (coverage === 'verified' && !controlType) {
    if ((asset.verifiedControlsCount ?? 0) <= 0) return false
  }
  // coverage === 'all' → no asset-level restriction

  if (controlType) {
    const row = rows.find((c) => c.controlType === controlType)
    if (!row) return false
    if (String(row.status || '').toUpperCase() === 'NOT_APPLICABLE') return false

    if (statusFilter) {
      if (String(row.status || '').toUpperCase() !== statusFilter) return false
    }

    if (coverage === 'known') {
      if (!isKnownStatus(row.status)) return false
    }
    if (coverage === 'verified') {
      if (!isVerifiedLike(row)) return false
    }

    if (verification) {
      if (!rowMatchesVerification(row, verification)) return false
    }
    if (quality) {
      if (!rowMatchesQuality(row, quality)) return false
    }
    return true
  }

  // No control selected — optional verification / quality across any applicable control
  if (verification) {
    const hit = rows.some(
      (r) =>
        String(r.status || '').toUpperCase() !== 'NOT_APPLICABLE' &&
        rowMatchesVerification(r, verification),
    )
    if (!hit) return false
  }
  if (quality) {
    const hit = rows.some(
      (r) =>
        String(r.status || '').toUpperCase() !== 'NOT_APPLICABLE' &&
        rowMatchesQuality(r, quality),
    )
    if (!hit) return false
  }

  // showSecurityControls alone → all assets (emphasize coverage UI)
  return true
}

export function filterAssetsBySecurityControlInventory<T extends AssetSecurityCoverageFields>(
  assets: T[],
  filters: SecurityControlInventoryFilters,
): T[] {
  if (!hasActiveSecurityControlFilters(filters)) return assets
  return assets.filter((a) => assetMatchesSecurityControlFilters(a, filters))
}

/** Clear only security-control related query params (preserve other inventory filters). */
export function clearSecurityControlParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  ;[
    'securityControl',
    'controlStatus',
    'securityCoverage',
    'verificationState',
    'sourceQuality',
    'showSecurityControls',
  ].forEach((k) => next.delete(k))
  return next
}

export function removeSecurityControlParam(
  params: URLSearchParams,
  key:
    | 'securityControl'
    | 'controlStatus'
    | 'securityCoverage'
    | 'verificationState'
    | 'sourceQuality'
    | 'showSecurityControls',
): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(key)
  // Drop dependent status/verification when control is cleared
  if (key === 'securityControl') {
    next.delete('controlStatus')
  }
  return next
}
